/**
 * Utility functions for variable mentions in textarea/contenteditable
 * Based on: https://craft.mxkaske.dev/post/fancy-area
 */

import type { WorkflowVariable } from "./extract-workflow-variables";

/**
 * Get caret coordinates in a textarea
 * Creates a duplicate div to measure the position
 */
export function getCaretCoordinates(element: HTMLTextAreaElement): { top: number; left: number; height: number } {
  const { value, selectionStart } = element;
  const div = document.createElement("div");
  const style = window.getComputedStyle(element);

  // Copy styles from textarea to div
  for (const prop of style) {
    (div.style as any)[prop] = style.getPropertyValue(prop);
  }

  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.whiteSpace = "pre-wrap";
  div.style.wordWrap = "break-word";

  // Set content up to caret position
  div.textContent = value.substring(0, selectionStart);

  // Add a span at the caret position to measure
  const span = document.createElement("span");
  span.textContent = value.substring(selectionStart) || ".";
  div.appendChild(span);

  document.body.appendChild(div);

  const coordinates = {
    top: span.offsetTop + parseInt(style.borderTopWidth),
    left: span.offsetLeft + parseInt(style.borderLeftWidth),
    height: parseInt(style.lineHeight),
  };

  document.body.removeChild(div);

  return coordinates;
}

/**
 * Get the current word where the caret is positioned
 */
export function getCurrentWord(element: HTMLTextAreaElement): string {
  const { value, selectionStart } = element;

  // Get text before caret
  const textBeforeCaret = value.substring(0, selectionStart);

  // Find the start of the current word (after last space or start of string)
  const words = textBeforeCaret.split(/\s/);
  const currentWord = words[words.length - 1] || "";

  return currentWord;
}

/**
 * Replace the current word where the caret is at with a new value
 */
export function replaceWord(element: HTMLTextAreaElement, newWord: string): void {
  const { value, selectionStart } = element;

  // Get text before and after caret
  const textBeforeCaret = value.substring(0, selectionStart);
  const textAfterCaret = value.substring(selectionStart);

  // Find where the current word starts
  const words = textBeforeCaret.split(/\s/);
  const currentWordStart = textBeforeCaret.length - (words[words.length - 1]?.length || 0);

  // Replace the word
  const newValue = value.substring(0, currentWordStart) + newWord + textAfterCaret;

  // Use execCommand for undo support (deprecated but still works)
  element.focus();
  element.setSelectionRange(currentWordStart, selectionStart);
  document.execCommand("insertText", false, newWord);

  // Set cursor after the inserted text
  const newPosition = currentWordStart + newWord.length;
  element.setSelectionRange(newPosition, newPosition);
}

// =============================================================================
// Contenteditable utilities
// =============================================================================

/**
 * Variable span attribute name for identifying variable elements
 */
export const VARIABLE_ATTR = "data-variable";

/**
 * Zero-width space character used to provide cursor anchor after variables
 */
export const ZERO_WIDTH_SPACE = "\u200B";

/**
 * Serialize contenteditable content to a plain string
 * Extracts text content and preserves variable references from data attributes
 */
export function serializeContentEditable(element: HTMLElement): string {
  let result = "";

  function traverse(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      // Strip zero-width spaces used for cursor anchoring
      result += (node.textContent || "").replace(/\u200B/g, "");
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;

      // Check if this is a variable span
      const variableAttr = el.getAttribute(VARIABLE_ATTR);
      if (variableAttr) {
        // Always use data-variable attribute to serialize the raw {{...}} path,
        // even when textContent shows a friendly display name
        result += variableAttr;
        return; // Don't recurse into variable spans
      }

      // Handle line breaks
      if (el.tagName === "BR") {
        result += "\n";
        return;
      }

      // Handle div/p as newlines (contenteditable creates these for Enter key)
      if ((el.tagName === "DIV" || el.tagName === "P") && result.length > 0 && !result.endsWith("\n")) {
        result += "\n";
      }

      // Recurse into children
      el.childNodes.forEach(child => traverse(child));
    }
  }

  element.childNodes.forEach(child => traverse(child));

  return result;
}

/**
 * Create a variable span element
 * @param displayText - Friendly text to show (e.g. "1. Manual Trigger: price"). Falls back to variableId.
 */
export function createVariableSpan(variableId: string, isValid: boolean, displayText?: string): HTMLSpanElement {
  const span = document.createElement("span");
  span.setAttribute(VARIABLE_ATTR, variableId);
  span.contentEditable = "false"; // Atomic pill - prevent editing inside
  span.title = variableId; // Raw path on hover as fallback

  // Apply styling classes
  if (isValid) {
    span.className =
      "variable-pill inline-flex items-center gap-0.5 rounded-md bg-blue-100 px-1.5 py-0 font-mono text-blue-700 ring-1 ring-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:ring-blue-900/40";
  } else {
    span.className =
      "variable-pill inline-flex items-center gap-0.5 rounded-md bg-transparent px-1.5 py-0 font-mono text-red-600 ring-1 ring-dashed ring-red-400 dark:text-red-400 dark:ring-red-500";
  }

  // Text content wrapper
  const textSpan = document.createElement("span");
  textSpan.className = "variable-text";
  textSpan.textContent = displayText || variableId;
  span.appendChild(textSpan);

  return span;
}

/**
 * Parse a string value into DOM nodes for contenteditable
 * Returns a DocumentFragment with text nodes and variable spans
 * @param getDisplayLabel - Optional callback to get friendly display text for a variable
 */
export function parseStringToFragment(
  value: string,
  availableVariables: WorkflowVariable[],
  isValidVariable: (variable: string, vars: WorkflowVariable[]) => boolean,
  getDisplayLabel?: (variableId: string) => string | undefined,
): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const variableRegex = /({{[^}]+}})/g;
  const parts = value.split(variableRegex);
  let lastNodeWasVariable = false;

  parts.forEach(part => {
    if (!part) return;

    if (part.startsWith("{{") && part.endsWith("}}")) {
      // This is a variable
      const isValid = isValidVariable(part, availableVariables);
      const displayText = getDisplayLabel?.(part);
      const span = createVariableSpan(part, isValid, displayText);
      fragment.appendChild(span);
      lastNodeWasVariable = true;
    } else {
      // Handle text with newlines
      const lines = part.split("\n");
      lines.forEach((line, index) => {
        if (line) {
          fragment.appendChild(document.createTextNode(line));
          lastNodeWasVariable = false;
        }
        // Add <br> for newlines (except after the last line)
        if (index < lines.length - 1) {
          fragment.appendChild(document.createElement("br"));
          lastNodeWasVariable = false;
        }
      });
    }
  });

  // Add ZWSP after last variable for cursor anchoring
  if (lastNodeWasVariable) {
    fragment.appendChild(document.createTextNode(ZERO_WIDTH_SPACE));
  }

  return fragment;
}

/**
 * Get the current word at cursor position in contenteditable
 * Returns the word and its start/end positions in the text
 */
export function getCurrentWordInContentEditable(element: HTMLElement): {
  word: string;
  startOffset: number;
  endOffset: number;
  textNode: Text | null;
} {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return { word: "", startOffset: 0, endOffset: 0, textNode: null };
  }

  const range = selection.getRangeAt(0);
  const node = range.startContainer;

  // Only handle text nodes
  if (node.nodeType !== Node.TEXT_NODE) {
    return { word: "", startOffset: 0, endOffset: 0, textNode: null };
  }

  const textNode = node as Text;
  const text = textNode.textContent || "";
  const cursorPos = range.startOffset;

  // Find word boundaries
  let startOffset = cursorPos;
  let endOffset = cursorPos;

  // Walk backward to find word start
  while (startOffset > 0 && !/\s/.test(text[startOffset - 1])) {
    startOffset--;
  }

  // Walk forward to find word end
  while (endOffset < text.length && !/\s/.test(text[endOffset])) {
    endOffset++;
  }

  const word = text.substring(startOffset, cursorPos);

  return { word, startOffset, endOffset, textNode };
}

/**
 * Insert a variable at the current cursor position, replacing any partial trigger text
 */
export function insertVariableAtCursor(
  element: HTMLElement,
  variableId: string,
  isValid: boolean,
  displayText?: string,
): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const { word, startOffset, textNode } = getCurrentWordInContentEditable(element);

  if (!textNode) {
    // No text node at cursor - just insert at selection
    const range = selection.getRangeAt(0);
    const span = createVariableSpan(variableId, isValid, displayText);
    // Add ZWSP after span for cursor anchoring
    const zwsp = document.createTextNode(ZERO_WIDTH_SPACE);

    range.deleteContents();
    range.insertNode(zwsp);
    range.insertNode(span);

    // Move cursor into the ZWSP text node
    range.setStart(zwsp, 1);
    range.setEnd(zwsp, 1);
    selection.removeAllRanges();
    selection.addRange(range);
    return;
  }

  // Replace the current word (which should be "{{..." trigger) with the variable
  const text = textNode.textContent || "";
  const cursorPos = selection.getRangeAt(0).startOffset;

  // Create the variable span
  const span = createVariableSpan(variableId, isValid, displayText);

  // Split the text node and insert the span
  const beforeText = text.substring(0, startOffset);
  const afterText = text.substring(cursorPos);

  // Clear and rebuild
  const parent = textNode.parentNode;
  if (!parent) return;

  // Create a fragment with: before text, variable span, after text (or ZWSP)
  const fragment = document.createDocumentFragment();
  if (beforeText) {
    fragment.appendChild(document.createTextNode(beforeText));
  }
  fragment.appendChild(span);

  // Add ZWSP for cursor anchoring if no text follows
  const zwsp = document.createTextNode(ZERO_WIDTH_SPACE);
  if (afterText) {
    fragment.appendChild(document.createTextNode(afterText));
  } else {
    fragment.appendChild(zwsp);
  }

  // Replace the text node with the fragment
  parent.replaceChild(fragment, textNode);

  // Place cursor after the span (in the ZWSP or afterText)
  const range = document.createRange();
  if (afterText) {
    range.setStartAfter(span);
    range.setEndAfter(span);
  } else {
    range.setStart(zwsp, 1);
    range.setEnd(zwsp, 1);
  }
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Check if the cursor is immediately after a variable span
 * Returns the variable span if found, null otherwise
 */
export function getVariableBeforeCursor(element: HTMLElement): HTMLSpanElement | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const node = range.startContainer;
  const offset = range.startOffset;

  // Case 1: Cursor is at the start of a text node - check previous sibling
  if (node.nodeType === Node.TEXT_NODE && offset === 0) {
    const prev = node.previousSibling;
    if (prev && prev.nodeType === Node.ELEMENT_NODE) {
      const el = prev as HTMLElement;
      if (el.hasAttribute(VARIABLE_ATTR)) {
        return el as HTMLSpanElement;
      }
    }
  }

  // Case 2: Cursor is directly after an element node
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;
    if (offset > 0) {
      const prevChild = el.childNodes[offset - 1];
      if (prevChild && prevChild.nodeType === Node.ELEMENT_NODE) {
        const prevEl = prevChild as HTMLElement;
        if (prevEl.hasAttribute(VARIABLE_ATTR)) {
          return prevEl as HTMLSpanElement;
        }
      }
    }
  }

  // Case 3: Cursor is in an empty, whitespace-only, or ZWSP-only text node
  // This handles the ZWSP we add after variables for cursor anchoring
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node as Text).textContent || "";
    // Strip ZWSP and whitespace to check if there's any "real" content before cursor
    const textBeforeCursor = text.substring(0, offset).replace(/[\u200B\s]/g, "");
    if (offset === 0 || textBeforeCursor === "") {
      let prev: Node | null = node.previousSibling;
      // Skip empty/ZWSP-only text nodes
      while (prev && prev.nodeType === Node.TEXT_NODE) {
        const prevText = ((prev as Text).textContent || "").replace(/[\u200B\s]/g, "");
        if (prevText) break;
        prev = prev.previousSibling;
      }
      if (prev && prev.nodeType === Node.ELEMENT_NODE) {
        const el = prev as HTMLElement;
        if (el.hasAttribute(VARIABLE_ATTR)) {
          return el as HTMLSpanElement;
        }
      }
    }
  }

  return null;
}

/**
 * Place cursor at the end of the contenteditable element
 */
export function placeCursorAtEnd(element: HTMLElement): void {
  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false); // collapse to end
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Save and restore selection for contenteditable
 */
export function saveSelection(element: HTMLElement): { start: number; end: number } | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);

  // Calculate offset from start of element
  const preCaretRange = document.createRange();
  preCaretRange.selectNodeContents(element);
  preCaretRange.setEnd(range.startContainer, range.startOffset);
  const start = preCaretRange.toString().length;

  preCaretRange.setEnd(range.endContainer, range.endOffset);
  const end = preCaretRange.toString().length;

  return { start, end };
}

export function restoreSelection(element: HTMLElement, savedSelection: { start: number; end: number }): void {
  const selection = window.getSelection();
  if (!selection) return;

  let charIndex = 0;
  const range = document.createRange();
  range.setStart(element, 0);
  range.collapse(true);

  let startSet = false;

  function traverse(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const textLength = node.textContent?.length || 0;
      const nextIndex = charIndex + textLength;

      if (!startSet && savedSelection.start >= charIndex && savedSelection.start <= nextIndex) {
        range.setStart(node, savedSelection.start - charIndex);
        startSet = true;
      }

      if (savedSelection.end >= charIndex && savedSelection.end <= nextIndex) {
        range.setEnd(node, savedSelection.end - charIndex);
        return true; // Done
      }

      charIndex = nextIndex;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.hasAttribute(VARIABLE_ATTR)) {
        // Variable spans count as their text content length
        const varText = el.getAttribute(VARIABLE_ATTR) || "";
        const nextIndex = charIndex + varText.length;

        if (!startSet && savedSelection.start >= charIndex && savedSelection.start <= nextIndex) {
          // Place cursor after the variable
          range.setStartAfter(el);
          startSet = true;
        }

        if (savedSelection.end >= charIndex && savedSelection.end <= nextIndex) {
          range.setEndAfter(el);
          return true;
        }

        charIndex = nextIndex;
      } else {
        for (const child of Array.from(node.childNodes)) {
          if (traverse(child)) return true;
        }
      }
    }
    return false;
  }

  traverse(element);

  selection.removeAllRanges();
  selection.addRange(range);
}
